import { ReactNode, createContext } from "react";
import useCommitment, {
    CommitmentResponse,
    CommitmentByWeekResponse,
    CommitmentsPaginationResponse,
    CreateCommitmentRequest,
    UpdateCommitmentRequest,
    RescheduleCommitmentRequest,
} from "./hooks/useCommitment";

interface CommitmentContextProviderProps {
    children: ReactNode;
}

export interface CommitmentContextData {
    commitments: CommitmentResponse[];
    weeklyCommitments: CommitmentByWeekResponse[];
    historic: CommitmentsPaginationResponse | null;
    nextCommitment: CommitmentResponse | null;

    createCommitment: (request: CreateCommitmentRequest) => Promise<any>;
    getCommitments: () => Promise<CommitmentResponse[] | null>;
    getByDate: (date: string) => Promise<CommitmentResponse[] | null>;
    getByWeek: (begin: string, end: string) => Promise<CommitmentByWeekResponse[] | null>;
    getById: (id: string) => Promise<CommitmentResponse | null>;
    getNext: () => Promise<CommitmentResponse | null>;
    getHistoric: (
        pageNumber: number,
        pageSize: number,
        leadId: string
    ) => Promise<CommitmentsPaginationResponse | null>;

    updateCommitment: (request: UpdateCommitmentRequest) => Promise<any>;
    rescheduleCommitment: (request: RescheduleCommitmentRequest) => Promise<any>;
    deleteCommitment: (id: string) => Promise<void>;

    setCommitments: (commitments: CommitmentResponse[]) => void;
    setWeeklyCommitments: (weekly: CommitmentByWeekResponse[]) => void;
}

const initialState: CommitmentContextData = {
    commitments: [],
    weeklyCommitments: [],
    historic: null,
    nextCommitment: null,

    createCommitment: async () => Promise.resolve(null),
    getCommitments: async () => Promise.resolve(null),
    getByDate: async () => Promise.resolve(null),
    getByWeek: async () => Promise.resolve(null),
    getById: async () => Promise.resolve(null),
    getNext: async () => Promise.resolve(null),
    getHistoric: async () => Promise.resolve(null),

    updateCommitment: async () => Promise.resolve(null),
    rescheduleCommitment: async () => Promise.resolve(null),
    deleteCommitment: async () => Promise.resolve(),

    setCommitments: () => { },
    setWeeklyCommitments: () => { },
};

export const CommitmentContext = createContext<CommitmentContextData>(initialState);

export default function CommitmentContextProvider({ children }: CommitmentContextProviderProps) {
    const {
        commitments,
        weeklyCommitments,
        historic,
        nextCommitment,
        createCommitment,
        getCommitments,
        getByDate,
        getByWeek,
        getById,
        getNext,
        getHistoric,
        updateCommitment,
        rescheduleCommitment,
        deleteCommitment,
        setCommitments,
        setWeeklyCommitments,
    } = useCommitment();

    return (
        <CommitmentContext.Provider
            value={{
                commitments,
                weeklyCommitments,
                historic,
                nextCommitment,
                createCommitment,
                getCommitments,
                getByDate,
                getByWeek,
                getById,
                getNext,
                getHistoric,
                updateCommitment,
                rescheduleCommitment,
                deleteCommitment,
                setCommitments,
                setWeeklyCommitments,
            }}
        >
            {children}
        </CommitmentContext.Provider>
    );
}